import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Receipt, Download, ArrowLeft } from 'lucide-react';
import UserService from "../services/user.service";
import { toast } from "react-toastify";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';

const ReceiptView = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [receipt, setReceipt] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const receiptRef = useRef<HTMLDivElement>(null);
  const userService = new UserService();

  useEffect(() => {
    const fetchReceipt = async () => {
      if (!id) {
        toast.error("Receipt ID is required");
        navigate('/parent/home');
        return;
      }

      try {
        setLoading(true);
        const response = await userService.getProgramReceipt(id);
        if (response.status && response.data) {
          setReceipt(response.data);
        } else {
          toast.error("Failed to load receipt");
          navigate('/parent/home');
        }
      } catch (error: any) {
        toast.error(error?.message || "Error loading receipt");
        navigate('/parent/home');
      } finally {
        setLoading(false);
      }
    };

    fetchReceipt();
  }, [id, navigate]);

  const formatCurrency = (amount: number, currency: string = "CAD") => {
    const code = String(currency || "CAD").toUpperCase();
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(Number(amount) || 0);
    return `${code} ${formatted}`;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleDownloadPDF = async () => {
    if (!receiptRef.current || !receipt) return;

    try {
      toast.info("Generating PDF...");
      const element = receiptRef.current;
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = 210; // A4 width in mm
      const pageHeight = 297; // A4 height in mm
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      const contentHeight = pageHeight - (margin * 2);
      
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        allowTaint: false
      } as any);

      const imgData = canvas.toDataURL('image/png', 0.95);
      
      // Calculate dimensions maintaining aspect ratio
      const imgWidth = contentWidth;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      // Scale down if content is too tall to fit on one page
      let finalWidth = imgWidth;
      let finalHeight = imgHeight;
      
      if (imgHeight > contentHeight) {
        finalHeight = contentHeight;
        finalWidth = (canvas.width * contentHeight) / canvas.height;
        // Center horizontally if scaled down
        const xOffset = (pageWidth - finalWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, margin, finalWidth, finalHeight);
      } else {
        // Center horizontally
        const xOffset = (pageWidth - finalWidth) / 2;
        pdf.addImage(imgData, 'PNG', xOffset, margin, finalWidth, finalHeight);
      }

      pdf.save(`receipt-${receipt.program?.id || 'receipt'}.pdf`);
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Error generating PDF:", error);
      toast.error("Failed to generate PDF");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-ryd-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading receipt...</p>
        </div>
      </div>
    );
  }

  if (!receipt) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Receipt className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-600">Receipt not found</p>
          <button
            onClick={() => navigate('/parent/home')}
            className="mt-4 px-4 py-2 bg-ryd-primary text-white rounded-lg hover:bg-opacity-90"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <button
            onClick={() => navigate('/parent/home')}
            className="flex items-center gap-2 text-gray-600 hover:text-ryd-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            <span>Back</span>
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center gap-2 px-6 py-2 bg-ryd-primary text-white rounded-lg hover:bg-opacity-90 transition-all shadow-lg hover:shadow-xl"
          >
            <Download className="w-5 h-5" />
            <span>Download PDF</span>
          </button>
        </div>

        {/* Receipt Card */}
        <div ref={receiptRef} className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header Section */}
          <div className="bg-gradient-to-r from-ryd-primary to-blue-600 text-white p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <Receipt className="w-8 h-8" />
                  <h1 className="text-3xl font-bold">Payment Receipt</h1>
                </div>
                <p className="text-blue-100 text-sm">RYD Learning Platform</p>
              </div>
              <div className="text-right">
                <p className="text-blue-100 text-sm">Receipt #</p>
                <p className="text-xl font-semibold">RYD-{receipt.program?.id || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <div className="p-8">
            {/* Program Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-ryd-primary">
                Program Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Program Title</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receipt.package?.title || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Level</p>
                  <p className="text-lg font-semibold text-gray-800">
                    Level {receipt.package?.level || "N/A"}
                  </p>
                </div>
                {receipt.cohort && (
                  <>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Cohort Title</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {receipt.cohort.title || "N/A"}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">Start Date</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatDate(receipt.cohort.startDate)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 mb-1">End Date</p>
                      <p className="text-lg font-semibold text-gray-800">
                        {formatDate(receipt.cohort.endDate)}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Date</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {formatDate(receipt.paymentDate)}
                  </p>
                </div>
              </div>
            </div>

            {/* Student & Parent Information */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-ryd-primary">
                Student & Parent Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Student Name</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receipt.child?.firstName} {receipt.child?.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Parent Name</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receipt.parent?.firstName} {receipt.parent?.lastName}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Parent Email</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receipt.parent?.email || "N/A"}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Country</p>
                  <p className="text-lg font-semibold text-gray-800">
                    {receipt.parent?.country || "N/A"}
                  </p>
                </div>
              </div>
            </div>

            {/* Payment Details - shows amount actually paid (after coupon if used) */}
            <div className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-ryd-primary">
                Payment Details
              </h2>
              <div className="bg-gray-50 rounded-lg p-6">
                <div className="space-y-4">
                  <div className="flex justify-between items-center py-2">
                    <span className="text-gray-600 text-lg">Package price:</span>
                    <span className="text-xl font-semibold text-gray-800">
                      {formatCurrency(
                        receipt.pricing?.originalAmount || 0,
                        receipt.pricing?.currency || "CAD"
                      )}
                    </span>
                  </div>

                  {receipt.coupon && receipt.pricing?.discount > 0 && (
                    <>
                      <div className="flex justify-between items-center py-2 border-t border-gray-200">
                        <div>
                          <span className="text-gray-600 text-lg">Coupon used</span>
                          <span className="text-sm text-gray-700 font-medium ml-2">
                            ({receipt.coupon.code})
                          </span>
                          {receipt.coupon.isPercentage && (
                            <span className="text-sm text-gray-500 ml-1">
                              — {receipt.coupon.value}% off
                            </span>
                          )}
                        </div>
                        <span className="text-xl font-semibold text-green-600">
                          -{formatCurrency(
                            receipt.pricing?.discount || 0,
                            receipt.pricing?.currency || "CAD"
                          )}
                        </span>
                      </div>
                    </>
                  )}

                  <div className="flex justify-between items-center py-4 border-t-2 border-ryd-primary mt-4">
                    <span className="text-2xl font-bold text-gray-800">Amount paid:</span>
                    <span className="text-3xl font-bold text-ryd-primary">
                      {formatCurrency(
                        receipt.pricing?.finalAmount ?? receipt.pricing?.originalAmount ?? 0,
                        receipt.pricing?.currency || "CAD"
                      )}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500">
                    This is the amount you actually paid{receipt.coupon && receipt.pricing?.discount > 0 ? " after applying your coupon" : ""}.
                  </p>

                  {receipt.pricing?.exchangeRate && receipt.pricing.exchangeRate !== 1 && (
                    <div className="text-xs text-gray-500 mt-2">
                      Exchange Rate: {receipt.pricing.exchangeRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t-2 border-gray-200 pt-6 mt-8">
              <div className="text-center text-gray-500 text-sm">
                <p className="mb-2">Thank you for your payment!</p>
                <p>This is an official receipt for your records.</p>
                <p className="mt-4 text-xs">
                  Generated on {new Date().toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit"
                  })}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReceiptView;

